import React, {createContext, ReactNode, useContext, useEffect, useState} from "react";
import {useQueryClient} from "react-query";
import {User} from "../pages/settings/users/useUser";
import {Facility} from "../pages/settings/facilities/useFacility";
import {redirectToLogin} from "../components/router/redirect";
import axiosClient from "../api/axios-client";
import {PermissionProvider} from "./PermissionContext";

// Clés localStorage pour persister le facility sélectionné et l'utilisateur
const SELECTED_FACILITY_KEY = 'SELECTED_FACILITY_ID';
const CURRENT_USER_KEY = 'CURRENT_USER_ID';

// Interfaces pour les données du contexte
interface Notification {
    success?: string;
    error?: string;
    warning?: string;
    info?: string;
}

interface IContextState {
    user: User;
    token: string | null;
    notifications: Notification | null;
    isUserLoading: boolean;
    isFacilitySwitching: boolean;
    currentFacility: Facility | null;
    setUser: (user: User) => void;
    setToken: (token: string | null) => void;
    setNotifications: (notifications: Notification | null) => void;
    setCurrentFacility: (facility: Facility | null) => void;
}

// Définir une valeur par défaut complète
export const defaultContextState: IContextState = {
    user: {
        id: 0,
        gender_id: 1,
        firstname: "John",
        lastname: "Doe",
        email: "john.doe@example.com",
        facility_id: 0,
        is_admin: false,
        is_super_admin: false,
        is_platform_owner: false,
        is_hotelier: false,
        facility: { id: 0,},
        customer_id: null,
        is_loyalty: false,
        loyalty_program_id: null,
        loyalty_points: 0,
        loyalty_level_points: 0,
        roles: [],
        permissions: [],
    },
    token: null,
    notifications: null,
    isUserLoading: false,
    isFacilitySwitching: false,
    currentFacility: null,
    setUser: () => {},
    setToken: () => {},
    setNotifications: () => {},
    setCurrentFacility: () => {},
};

// Type pour les enfants dans les props du fournisseur de contexte
type ContextProviderProps = {
    children: ReactNode;
};

// Contexte avec la valeur par défaut spécifiée
const StateContext = createContext<IContextState>(defaultContextState);

// Fournisseur de contexte qui utilise le hook useState pour gérer l'utilisateur et les tokens
export const ContextProvider = ({ children }: ContextProviderProps) => {
    const queryClient = useQueryClient();

    const [user, setUser] = useState<User>(defaultContextState.user);
    const [token, setToken] = useState<string | null>(localStorage.getItem('ACCESS_TOKEN'));
    const [notifications, _setNotifications] = useState<Notification|null>(null);
    const [isUserLoading, setIsUserLoading] = useState(false);
    const [isFacilitySwitching, setIsFacilitySwitching] = useState(false);
    const [currentFacility, _setCurrentFacility] = useState<Facility | null>(null);

    useEffect(() => {
        // Liste des routes publiques
        const publicRoutes = [
            "/login",
            "/signup",
            "/forgot-password",
            "/reset-password",
        ];

        // Vérifie si l'utilisateur est sur une route publique ou un reset-password dynamique
        const currentPath = window.location.pathname;
        const isPublicRoute =
            publicRoutes.includes(currentPath) ||
            currentPath.startsWith("/reset-password/")||
            // L'invité n'a pas encore de compte ouvert : sans cette ligne il
            // serait renvoyé vers /login avant d'avoir vu sa page.
            currentPath.startsWith("/invitation/")||
            currentPath.startsWith("/survey-response/")||
            currentPath.startsWith("/public-document/")||
            currentPath.startsWith("/locks/")||
            currentPath.startsWith("/kiosk/")||
            currentPath.startsWith("/report-export/");

        // Redirige uniquement si pas de token et route non publique
        if (!token && !isPublicRoute) {
            redirectToLogin();
        }
    }, [token]);

    useEffect(() => {
        if (!token) {
            setUser(defaultContextState.user);
            _setCurrentFacility(null);
            return;
        }
        setIsUserLoading(true);
        axiosClient.get('/user')
            .then(async ({data}) => {
                setUser(data);

                // Vérifier si l'utilisateur a changé (nouvelle session)
                const savedUserId = localStorage.getItem(CURRENT_USER_KEY);
                const currentUserId = data.id?.toString();

                if (savedUserId && savedUserId !== currentUserId) {
                    // L'utilisateur a changé, on nettoie le facility sauvegardé
                    localStorage.removeItem(SELECTED_FACILITY_KEY);
                }

                // Sauvegarder l'ID de l'utilisateur courant
                if (currentUserId) {
                    localStorage.setItem(CURRENT_USER_KEY, currentUserId);
                }

                // Récupérer le facility ID sauvegardé dans localStorage
                const savedFacilityId = localStorage.getItem(SELECTED_FACILITY_KEY);

                // Si un facility est sauvegardé et différent du facility de l'user
                if (savedFacilityId && data.facility && savedFacilityId !== data.facility.id.toString()) {
                    try {
                        // Essayer de récupérer le facility sauvegardé
                        const facilityResponse = await axiosClient.get(`/facilities/${savedFacilityId}`);
                        _setCurrentFacility(facilityResponse.data);
                    } catch (error) {
                        // Si le facility n'est plus accessible (403 ou autre), utiliser celui de l'user
                        console.warn('Saved facility no longer accessible, using user facility');
                        localStorage.removeItem(SELECTED_FACILITY_KEY);
                        if (data.facility) {
                            _setCurrentFacility(data.facility);
                        }
                    }
                } else if (data.facility) {
                    // Pas de facility sauvegardé ou identique à celui de l'user
                    _setCurrentFacility(data.facility);
                }
            })
            .catch(() => {
                // token invalide → on nettoie et on renvoie login
                setToken(null);
                redirectToLogin();
            })
            .finally(() => setIsUserLoading(false));
    }, [token]);

    const setNotifications = (notifications: Notification|null) => {
        _setNotifications(notifications);
        // Durée plus longue pour les erreurs (messages souvent plus longs à lire)
        const duration = notifications?.error ? 6000 : 2000;
        setTimeout(() => {
            _setNotifications(null);
        }, duration);
    };

    const handleSetUser = (user: User) => {
        setUser(user);
    };

    const handleSetToken = (token: string | null) => {
        setToken(token);
        if (token) {
            localStorage.setItem('ACCESS_TOKEN', token);
        } else {
            localStorage.removeItem('ACCESS_TOKEN');
            localStorage.removeItem(SELECTED_FACILITY_KEY);
            localStorage.removeItem(CURRENT_USER_KEY);
            _setCurrentFacility(null);
        }
    };

    const handleSetCurrentFacility = async (facility: Facility | null) => {
        const previousFacilityId = currentFacility?.id;
        _setCurrentFacility(facility);

        // Sauvegarder l'ID du facility sélectionné dans le localStorage
        if (facility) {
            localStorage.setItem(SELECTED_FACILITY_KEY, facility.id.toString());
        } else {
            localStorage.removeItem(SELECTED_FACILITY_KEY);
        }

        // Invalider toutes les queries si le facility a changé pour forcer le rechargement des données
        if (facility && previousFacilityId !== facility.id) {
            setIsFacilitySwitching(true);
            await queryClient.invalidateQueries();
            // Petit délai pour laisser le temps aux queries de se relancer
            setTimeout(() => {
                setIsFacilitySwitching(false);
            }, 500);
        }
    };

    return (
        <StateContext.Provider value={{
            user,
            token,
            notifications,
            isUserLoading,
            isFacilitySwitching,
            currentFacility,
            setUser: handleSetUser,
            setToken: handleSetToken,
            setNotifications,
            setCurrentFacility: handleSetCurrentFacility,
        }}>
            <PermissionProvider
                permissions={user.permissions || []}
                roles={user.roles || []}
                isSuperAdmin={Boolean(user.is_super_admin)}
                isAdmin={Boolean(user.is_admin)}
                isGroupAdmin={Boolean(user.facility?.is_group)}
            >
                {children}
            </PermissionProvider>
        </StateContext.Provider>
    );
};


// Hook personnalisé pour utiliser le contexte
export const useStateContext = () => useContext(StateContext);
